import { luminance, readableTextOn, styleColumnsToPalette, themeCssVars } from './colorRoles';

/**
 * The page's own background and text colour: what every transparent section shows through.
 *
 * The live layout paints these on the page wrapper; the Studio preview paints them on its frame.
 * They used to be decided separately. Live took the site's `color_background`, while the Studio
 * frame was hardcoded white unless a dark look was on. A site with a dark background and a text
 * section (transparent by design) therefore showed dark text on white in the Studio, and the same
 * dark text on a dark page once published: an empty dark band where the words should be.
 *
 * `vars` is the look / dark-mode / template-override layer the caller has already merged (never the
 * brand palette itself), in that order; the site's own style columns are the fallback.
 */
export function pageSurfaceColors(
  vars: Record<string, string | undefined> | null | undefined,
  style: { color_background?: string | null; color_text?: string | null } | null | undefined,
): { backgroundColor: string | undefined; color: string | undefined } {
  return {
    backgroundColor: vars?.['--color-background'] ?? style?.color_background ?? undefined,
    color: vars?.['--color-text'] ?? style?.color_text ?? undefined,
  };
}

/** WCAG contrast ratio between two hexes, or null when either is unparseable. */
function contrastRatio(a: string, b: string): number | null {
  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The colour a listing card's price is painted in: Primary (Brand Manager says so — "Primary
 * paints … listing prices"), unless Primary cannot be read on the card, in which case Text Primary.
 *
 * Reported twice (2026-10-19, Homes in Paradise SWFL): a brand with a white Primary — chosen for the
 * header — put a white price on a near-white card, so the price vanished. 3:1 is the WCAG minimum
 * for large/bold text, which the price is. `vars` is the merged page layer (look surface, dark mode,
 * template_overrides) and `palette` the site's own resolved vars, so the check uses the card colour
 * the visitor actually sees.
 */
export function priceColor(
  vars: Record<string, string | undefined> | null | undefined,
  palette: Record<string, string | undefined>,
): string | undefined {
  const pick = (k: string) => vars?.[k] ?? palette[k];
  const primary = pick('--color-primary');
  const text = pick('--color-text');
  if (!primary) return undefined;
  // A card price sits on Surface; the listing page's price sits on the page Background. Both read
  // the one var, so Primary must be readable on both (2026-10-21: a yellow Primary on a white
  // listing page, chosen when the site's own page colours were dark navy).
  const ratios = [pick('--color-surface'), pick('--color-background')]
    .map((bg) => (bg ? contrastRatio(primary, bg) : null))
    .filter((r): r is number => r !== null);
  return ratios.some((r) => r < 3) && text ? text : primary;
}

/**
 * How every listing price is painted: the var `priceColor` fills on the page wrapper (layout.tsx),
 * with Primary as the fallback for a page rendered without it. One constant, so a card or page
 * written later cannot paint its price straight from Primary again — the listing page did, and so
 * showed a yellow price on white while the cards were fixed.
 */
export const LISTING_PRICE_COLOR = 'var(--color-price, var(--color-primary))';

/** `#rgb`/`#rrggbb` → `#rrggbb`, or null. */
function fullHex(c: string | undefined): string | null {
  const h = (c ?? '').trim().replace('#', '');
  const full = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  return /^[0-9a-fA-F]{6}$/.test(full) ? `#${full.toLowerCase()}` : null;
}

/** `share` of `a` over `b`, both hexes (what CSS `color-mix(in srgb, a share, b)` paints). */
function mixHex(a: string, b: string, share: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(1 + i, 3 + i), 16);
  return `#${[0, 2, 4]
    .map((i) => Math.round(ch(a, i) * share + ch(b, i) * (1 - share)).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** The four vars `listingInkVars` may set (read by the site-ink layer, packages/blocks cardStyles.css). */
export const LISTING_INK_VARS = ['--color-ink-page', '--color-ink-muted-page', '--color-ink-surface', '--color-ink-muted-surface'] as const;

/**
 * The words a listing page or card paints, made readable on what they sit on.
 *
 * Reported 2026-10-22 (Simon Gray Realty): the site's Surface was a dark navy (#1a1d23) while its
 * Text Primary stayed #333333 and Text Secondary was white — fine on the white page, but the
 * listing page's panels, sticky bar and cards are painted in Surface, so their headings and values
 * were dark grey on navy and only the (Text Secondary) bullets showed. The same white Text
 * Secondary would vanish on the white page.
 *
 * So each surface gets its own pair, both held to 4.5:1 (WCAG body text — the muted colour paints
 * 11–14px labels and city lines, so the large-text 3:1 is not enough): the site's Text Primary
 * where it reads, else the first that reads of the readable ink for that background
 * (`readableTextOn`, which honours the brand's Text on dark), Text on dark itself, white, near
 * black — a luminance cutoff alone can pick white on a mid-grey; the site's Text Secondary where it
 * reads, else the chosen ink softened 72% toward the background, else the ink itself. Only the
 * vars that differ from the site's own colours are returned, so a site whose colours already read
 * is painted exactly as before. Nothing is written back to the site's colours.
 *
 * `reset`: a wrapper that re-paints the palette below the page wrapper (a home look, a previewed
 * search look) must also clear the page's corrections it does not need — spreading an empty result
 * would leave the page's inherited, so the unneeded vars come back as `initial` (CSS's
 * guaranteed-invalid value, which sends var() to its fallback: the site's own Text colours).
 */
export function listingInkVars(
  vars: Record<string, string | undefined> | null | undefined,
  palette: Record<string, string | undefined>,
  opts: { reset?: boolean } = {},
): Record<string, string> {
  const pick = (k: string) => vars?.[k] ?? palette[k];
  const text = fullHex(pick('--color-text'));
  const muted = fullHex(pick('--color-text-muted'));
  const onDark = pick('--color-text-on-dark');
  const out: Record<string, string> = {};
  for (const [where, bgVar] of [['page', '--color-background'], ['surface', '--color-surface']] as const) {
    const bg = fullHex(pick(bgVar));
    if (!bg) continue;
    const ratio = (c: string | null) => (c === null ? 0 : contrastRatio(c, bg) ?? 0);
    const reads = (c: string | null) => ratio(c) >= 4.5;
    let ink: string;
    if (text !== null && reads(text)) {
      ink = text;
    } else {
      const candidates = [readableTextOn(bg, onDark), onDark, '#ffffff', '#111827']
        .map((c) => fullHex(c ?? undefined))
        .filter((c): c is string => c !== null);
      ink = candidates.find(reads) ?? candidates.reduce((a, b) => (ratio(b) > ratio(a) ? b : a));
    }
    let quiet: string = muted ?? ink;
    if (!reads(muted)) {
      const soft = mixHex(ink, bg, 0.72);
      quiet = reads(soft) ? soft : ink;
    }
    if (ink !== text) out[`--color-ink-${where}`] = ink;
    if (quiet !== muted) out[`--color-ink-muted-${where}`] = quiet;
  }
  if (opts.reset) for (const k of LISTING_INK_VARS) out[k] ??= 'initial';
  return out;
}

/**
 * `listingInkVars` for a wrapper that re-paints the palette below the page wrapper, from the
 * wrapper's vars over the site's own colours (the same palette the layout builds), with `reset`.
 */
export function wrapperInkVars(
  wrapperVars: Record<string, string | undefined>,
  style: (Record<string, unknown> & { colors?: Record<string, string> | null }) | null | undefined,
): Record<string, string> {
  const palette = themeCssVars({ ...styleColumnsToPalette(style ?? {}), ...(style?.colors ?? {}) });
  return listingInkVars(wrapperVars, palette, { reset: true });
}

/** Words on a Surface-painted listing card, readable whatever the site's Text colour (`listingInkVars`). */
export const SURFACE_TEXT_COLOR = 'var(--color-ink-surface, var(--color-text))';
export const SURFACE_MUTED_COLOR = 'var(--color-ink-muted-surface, var(--color-text-muted))';
